import { useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Input,
    Select,
    SelectItem,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Tooltip,
} from "@heroui/react";
import { FolderSimple, Plus, Trash, Tag, X } from "@phosphor-icons/react";
import { CompanyDocumentLabel, CompanyFolderStructure } from "shared/types/company-documents";

// ─── Inline label creator ────────────────────────────────────────────────────

interface AddLabelPopoverProps {
    folderId: number;
    folderName: string;
    onCreated: () => void;
}

const AddLabelPopover = ({ folderId, folderName, onCreated }: AddLabelPopoverProps) => {
    const { session } = useSession();
    const [isOpen, setIsOpen] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const reset = () => {
        setName("");
        setDescription("");
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            toast.error("Geef het label een naam");
            return;
        }
        setIsSaving(true);
        try {
            const body: Record<string, unknown> = {
                label: name.trim(),
                folderStructureId: folderId,
            };
            if (description.trim()) body.description = description.trim();

            const response = await fetchWithAuth(
                `${API_URL}/company-documents/labels`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                },
                session,
            );
            if (!response.ok) {
                let message = "Fout bij aanmaken van label";
                try {
                    const err = await response.json();
                    if (err?.message)
                        message = Array.isArray(err.message) ? err.message.join(", ") : err.message;
                } catch {
                    /* ignore parse errors */
                }
                throw new Error(message);
            }
            toast.success("Label toegevoegd");
            reset();
            setIsOpen(false);
            onCreated();
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error creating company document label:", error);
            toast.error(error instanceof Error ? error.message : "Fout bij aanmaken van label");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Popover
            isOpen={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open);
                if (!open) reset();
            }}
            placement="bottom-end"
        >
            <PopoverTrigger>
                <button
                    type="button"
                    className="text-moss-primary border-moss-primary/40 hover:bg-moss-primary/5 hover:border-moss-primary inline-flex h-fit min-w-fit items-center gap-1 rounded-full border border-dashed bg-white px-2 py-1 text-xs font-medium transition-colors"
                    aria-label={`Label toevoegen aan ${folderName}`}
                >
                    <Plus size={12} weight="bold" />
                    Label toevoegen
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-3">
                <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-0.5">
                        <p className="text-neutral-primary text-sm font-semibold">
                            Nieuw label
                        </p>
                        <p className="text-neutral-secondary-mid text-xs">
                            in map{" "}
                            <span className="text-neutral-primary font-medium">
                                {folderName}
                            </span>
                        </p>
                    </div>
                    <Input
                        size="sm"
                        autoFocus
                        label="Naam"
                        placeholder="bv. Notarisakte"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSubmit();
                        }}
                        classNames={{ label: "text-xs", input: "text-sm" }}
                    />
                    <Input
                        size="sm"
                        label="Beschrijving (optioneel)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSubmit();
                        }}
                        classNames={{ label: "text-xs", input: "text-sm" }}
                    />
                    <div className="mt-1 flex justify-end gap-2">
                        <Button
                            size="sm"
                            variant="light"
                            onPress={() => setIsOpen(false)}
                            isDisabled={isSaving}
                        >
                            Annuleren
                        </Button>
                        <Button
                            size="sm"
                            className="bg-moss-primary text-white"
                            onPress={handleSubmit}
                            isDisabled={isSaving}
                        >
                            Toevoegen
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

// ─── Editable label chip ─────────────────────────────────────────────────────

interface EditableLabelChipProps {
    label: CompanyDocumentLabel;
    onUpdated: () => void;
    onRequestDelete: (label: CompanyDocumentLabel) => void;
}

const EditableLabelChip = ({ label, onUpdated, onRequestDelete }: EditableLabelChipProps) => {
    const { session } = useSession();
    const [isOpen, setIsOpen] = useState(false);
    const [name, setName] = useState(label.label);
    const [description, setDescription] = useState(label.description || "");
    const [isSaving, setIsSaving] = useState(false);

    const reset = () => {
        setName(label.label);
        setDescription(label.description || "");
    };

    const handleSave = async () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            toast.error("Geef het label een naam");
            return;
        }
        if (
            trimmedName === label.label &&
            description.trim() === (label.description || "")
        ) {
            setIsOpen(false);
            return;
        }
        setIsSaving(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/company-documents/labels/${label.id}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        label: trimmedName,
                        description: description.trim() || null,
                    }),
                },
                session,
            );
            if (!response.ok) {
                let message = "Fout bij bijwerken van label";
                try {
                    const err = await response.json();
                    if (err?.message)
                        message = Array.isArray(err.message)
                            ? err.message.join(", ")
                            : err.message;
                } catch {
                    /* ignore parse errors */
                }
                throw new Error(message);
            }
            toast.success("Label bijgewerkt");
            setIsOpen(false);
            onUpdated();
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error updating company document label:", error);
            toast.error(error instanceof Error ? error.message : "Fout bij bijwerken van label");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Popover
            isOpen={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open);
                if (!open) reset();
            }}
            placement="bottom-start"
        >
            <Tooltip
                content={label.description || "Klik om te bewerken"}
                placement="top"
                isDisabled={isOpen}
            >
                <div className="text-neutral-primary inline-flex h-fit min-w-fit items-center gap-1 rounded-full border border-neutral-200 bg-white pl-2 pr-1 text-xs font-medium">
                    <Tag size={12} weight="duotone" className="text-moss-primary" />
                    <PopoverTrigger>
                        <button
                            type="button"
                            className="hover:text-moss-primary py-1 transition-colors"
                            aria-label={`Label ${label.label} bewerken`}
                        >
                            {label.label}
                        </button>
                    </PopoverTrigger>
                    <button
                        type="button"
                        onClick={() => onRequestDelete(label)}
                        className="text-neutral-secondary-mid ml-0.5 rounded-full p-0.5 transition-colors hover:bg-red-50 hover:text-red-600"
                        aria-label={`Label ${label.label} verwijderen`}
                    >
                        <X size={12} weight="bold" />
                    </button>
                </div>
            </Tooltip>
            <PopoverContent className="w-72 p-3">
                <div className="flex flex-col gap-2">
                    <p className="text-neutral-primary text-sm font-semibold">Label bewerken</p>
                    <Input
                        size="sm"
                        autoFocus
                        label="Naam"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSave();
                        }}
                        classNames={{ label: "text-xs", input: "text-sm" }}
                    />
                    <Input
                        size="sm"
                        label="Beschrijving (optioneel)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSave();
                        }}
                        classNames={{ label: "text-xs", input: "text-sm" }}
                    />
                    <div className="mt-1 flex justify-end gap-2">
                        <Button
                            size="sm"
                            variant="light"
                            onPress={() => {
                                reset();
                                setIsOpen(false);
                            }}
                            isDisabled={isSaving}
                        >
                            Annuleren
                        </Button>
                        <Button
                            size="sm"
                            className="bg-moss-primary text-white"
                            onPress={handleSave}
                            isDisabled={isSaving}
                        >
                            Opslaan
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

// ─── Edit folder popover ─────────────────────────────────────────────────────

const collectDescendantIds = (folder: CompanyFolderStructure): number[] => {
    const ids: number[] = [];
    const walk = (node: CompanyFolderStructure) => {
        for (const child of node.children || []) {
            ids.push(child.id);
            walk(child);
        }
    };
    walk(folder);
    return ids;
};

interface EditFolderPopoverProps {
    folder: CompanyFolderStructure;
    flatFolders: { id: number; label: string }[];
    onUpdated: () => void;
}

const EditFolderPopover = ({
    folder,
    flatFolders,
    onUpdated,
}: EditFolderPopoverProps) => {
    const { session } = useSession();
    const [isOpen, setIsOpen] = useState(false);
    const [name, setName] = useState(folder.name);
    const [description, setDescription] = useState(folder.description || "");
    const [parentId, setParentId] = useState<string>(
        folder.parentId ? folder.parentId.toString() : "",
    );
    const [isSaving, setIsSaving] = useState(false);

    const reset = () => {
        setName(folder.name);
        setDescription(folder.description || "");
        setParentId(folder.parentId ? folder.parentId.toString() : "");
    };

    // Forbid making this folder a child of itself or any of its descendants
    const forbiddenParentIds = useMemo(() => {
        const descendants = collectDescendantIds(folder);
        return new Set<number>([folder.id, ...descendants]);
    }, [folder]);

    const validParentOptions = useMemo(
        () => flatFolders.filter((f) => !forbiddenParentIds.has(f.id)),
        [flatFolders, forbiddenParentIds],
    );

    const handleSave = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            toast.error("Geef de map een naam");
            return;
        }
        const nextParentId = parentId ? parseInt(parentId, 10) : null;
        if (nextParentId !== null && forbiddenParentIds.has(nextParentId)) {
            toast.error("Een map kan niet onder zichzelf staan");
            return;
        }

        const noChanges =
            trimmed === folder.name &&
            description.trim() === (folder.description || "") &&
            nextParentId === (folder.parentId ?? null);
        if (noChanges) {
            setIsOpen(false);
            return;
        }

        setIsSaving(true);
        try {
            const body: Record<string, unknown> = {
                name: trimmed,
                description: description.trim() || null,
                parentId: nextParentId,
            };
            const response = await fetchWithAuth(
                `${API_URL}/company-documents/folder-structure/${folder.id}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                },
                session,
            );
            if (!response.ok) {
                let message = "Fout bij bijwerken van map";
                try {
                    const err = await response.json();
                    if (err?.message)
                        message = Array.isArray(err.message)
                            ? err.message.join(", ")
                            : err.message;
                } catch {
                    /* ignore */
                }
                throw new Error(message);
            }
            toast.success("Map bijgewerkt");
            setIsOpen(false);
            onUpdated();
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error updating folder:", error);
            toast.error(
                error instanceof Error ? error.message : "Fout bij bijwerken van map",
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Popover
            isOpen={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open);
                if (!open) reset();
            }}
            placement="bottom-start"
        >
            <PopoverTrigger>
                <button
                    type="button"
                    className="text-neutral-primary hover:text-moss-primary inline-flex items-center gap-1.5 rounded font-medium transition-colors"
                    aria-label={`Map ${folder.name} bewerken`}
                >
                    <FolderSimple
                        className="text-moss-primary h-[18px] w-[18px]"
                        weight="duotone"
                    />
                    <span>{folder.name}</span>
                    {folder.description && (
                        <span className="text-neutral-secondary-mid text-sm font-normal">
                            — {folder.description}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3">
                <div className="flex flex-col gap-2">
                    <p className="text-neutral-primary text-sm font-semibold">
                        Map bewerken
                    </p>
                    <Input
                        size="sm"
                        autoFocus
                        label="Naam"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSave();
                        }}
                        classNames={{ label: "text-xs", input: "text-sm" }}
                    />
                    <Input
                        size="sm"
                        label="Beschrijving (optioneel)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSave();
                        }}
                        classNames={{ label: "text-xs", input: "text-sm" }}
                    />
                    <Select
                        size="sm"
                        label="Bovenliggende map"
                        placeholder="Geen (root)"
                        selectedKeys={parentId ? [parentId] : []}
                        onChange={(e) => setParentId(e.target.value)}
                        variant="bordered"
                        classNames={{ label: "text-xs", value: "text-sm" }}
                    >
                        {[
                            <SelectItem key="" textValue="Geen (root)">
                                <span className="text-neutral-secondary-mid italic">
                                    Geen (root)
                                </span>
                            </SelectItem>,
                            ...validParentOptions.map((f) => (
                                <SelectItem
                                    key={f.id.toString()}
                                    textValue={f.label.trim()}
                                >
                                    <span className="font-mono text-sm">{f.label}</span>
                                </SelectItem>
                            )),
                        ]}
                    </Select>
                    <div className="mt-1 flex justify-end gap-2">
                        <Button
                            size="sm"
                            variant="light"
                            onPress={() => {
                                reset();
                                setIsOpen(false);
                            }}
                            isDisabled={isSaving}
                        >
                            Annuleren
                        </Button>
                        <Button
                            size="sm"
                            className="bg-moss-primary text-white"
                            onPress={handleSave}
                            isDisabled={isSaving}
                        >
                            Opslaan
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

// ─── Node Component ──────────────────────────────────────────────────────────

interface CompanyFolderStructureNodeProps {
    folder: CompanyFolderStructure;
    level: number;
    allLabels: CompanyDocumentLabel[];
    flatFolders: { id: number; label: string }[];
    refetch: () => void;
    onRequestLabelDelete: (label: CompanyDocumentLabel) => void;
    onRequestFolderDelete: (folder: CompanyFolderStructure) => void;
}

const CompanyFolderStructureNode = ({
    folder,
    level,
    allLabels,
    flatFolders,
    refetch,
    onRequestLabelDelete,
    onRequestFolderDelete,
}: CompanyFolderStructureNodeProps) => {
    const children = folder.children || [];
    const assignedLabels = allLabels.filter((l) => l.folderStructureId === folder.id);

    return (
        <section className="flex flex-col gap-2">
            <section
                className="flex flex-col gap-1.5"
                style={{ marginLeft: `${level * 20}px` }}
            >
                <header className="flex items-center justify-between">
                    <EditFolderPopover
                        folder={folder}
                        flatFolders={flatFolders}
                        onUpdated={refetch}
                    />
                    <Tooltip content="Map verwijderen" placement="top">
                        <button
                            onClick={() => onRequestFolderDelete(folder)}
                            className="text-neutral-secondary-mid hover:text-red-600 hover:bg-red-50 rounded p-1 transition-colors"
                            aria-label="Map verwijderen"
                        >
                            <Trash size={15} />
                        </button>
                    </Tooltip>
                </header>

                <div className="flex flex-wrap items-center gap-1.5 pl-6">
                    {assignedLabels.map((label) => (
                        <EditableLabelChip
                            key={label.id}
                            label={label}
                            onUpdated={refetch}
                            onRequestDelete={onRequestLabelDelete}
                        />
                    ))}
                    <AddLabelPopover
                        folderId={folder.id}
                        folderName={folder.name}
                        onCreated={refetch}
                    />
                </div>
            </section>

            {children.map((child) => (
                <CompanyFolderStructureNode
                    key={child.id}
                    folder={child}
                    level={level + 1}
                    allLabels={allLabels}
                    flatFolders={flatFolders}
                    refetch={refetch}
                    onRequestLabelDelete={onRequestLabelDelete}
                    onRequestFolderDelete={onRequestFolderDelete}
                />
            ))}
        </section>
    );
};

// ─── Parent Tab Component ─────────────────────────────────────────────────────

interface NewFolderForm {
    name: string;
    description: string;
    parentId: string;
}

const emptyFolderForm: NewFolderForm = { name: "", description: "", parentId: "" };

export const CompanyFolderStructureTab = () => {
    const { session } = useSession();
    const [folders, setFolders] = useState<CompanyFolderStructure[]>([]);
    const [labels, setLabels] = useState<CompanyDocumentLabel[]>([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newFolder, setNewFolder] = useState<NewFolderForm>(emptyFolderForm);
    const [isLoading, setIsLoading] = useState(false);

    const [labelToDelete, setLabelToDelete] = useState<CompanyDocumentLabel | null>(null);
    const [folderToDelete, setFolderToDelete] = useState<CompanyFolderStructure | null>(null);

    useEffect(() => {
        fetchFolders();
        fetchLabels();
    }, []);

    const fetchFolders = async () => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/company-documents/folder-structure`,
                { method: "GET" },
                session,
            );
            if (!response.ok)
                throw new Error(`Failed to fetch folder structure: ${response.status}`);
            const data = await response.json();
            setFolders(data);
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error fetching company folder structure:", error);
            toast.error("Fout bij ophalen van mappenstructuur");
        }
    };

    const fetchLabels = async () => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/company-documents/labels`,
                { method: "GET" },
                session,
            );
            if (!response.ok) throw new Error(`Failed to fetch labels: ${response.status}`);
            const data = await response.json();
            setLabels(data);
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error fetching company document labels:", error);
            toast.error("Fout bij ophalen van labels");
        }
    };

    const handleAddFolder = async () => {
        if (!newFolder.name.trim()) {
            toast.error("Geef de map een naam");
            return;
        }
        setIsLoading(true);
        try {
            const body: Record<string, unknown> = { name: newFolder.name.trim() };
            if (newFolder.description.trim()) body.description = newFolder.description.trim();
            if (newFolder.parentId) body.parentId = parseInt(newFolder.parentId);

            const response = await fetchWithAuth(
                `${API_URL}/company-documents/folder-structure`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                },
                session,
            );
            if (!response.ok) throw new Error(`Failed to create folder: ${response.status}`);
            toast.success("Map succesvol aangemaakt");
            setIsAddModalOpen(false);
            setNewFolder(emptyFolderForm);
            await fetchFolders();
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error creating company folder:", error);
            toast.error("Fout bij aanmaken van map");
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmLabelDelete = async () => {
        if (!labelToDelete) return;
        setIsLoading(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/company-documents/labels/${labelToDelete.id}`,
                { method: "DELETE" },
                session,
            );
            if (!response.ok) throw new Error(`Failed to delete label: ${response.status}`);
            toast.success("Label verwijderd");
            setLabelToDelete(null);
            await fetchLabels();
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error deleting company document label:", error);
            toast.error("Fout bij verwijderen van label");
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmFolderDelete = async () => {
        if (!folderToDelete) return;
        setIsLoading(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/company-documents/folder-structure/${folderToDelete.id}`,
                { method: "DELETE" },
                session,
            );
            if (!response.ok) throw new Error(`Failed to delete folder: ${response.status}`);
            toast.success("Map verwijderd");
            setFolderToDelete(null);
            await fetchFolders();
            await fetchLabels();
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error deleting company folder:", error);
            toast.error("Fout bij verwijderen van map");
        } finally {
            setIsLoading(false);
        }
    };

    const handleRefetch = () => {
        fetchFolders();
        fetchLabels();
    };

    const flatFolders = flattenFolders(folders);

    return (
        <div className="flex flex-col gap-4">
            <header className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-neutral-primary text-lg font-medium">
                        Vennootschapsmappen
                    </h3>
                    <p className="text-neutral-secondary-mid mt-1 text-sm">
                        Definieer hier de mappenstructuur en koppel labels rechtstreeks aan een
                        map. De structuur wordt automatisch in SharePoint aangemaakt wanneer je
                        bij een vennootschap op 'Mappenstructuur aanmaken' klikt.
                    </p>
                </div>
                <Button
                    className="bg-moss-primary text-white"
                    onClick={() => setIsAddModalOpen(true)}
                    startContent={<Plus size={16} />}
                >
                    Map toevoegen
                </Button>
            </header>

            <div className="flex flex-col gap-3 rounded-lg border border-neutral-200 bg-white p-4">
                {folders.length === 0 ? (
                    <p className="text-neutral-secondary-mid py-6 text-center text-sm">
                        Nog geen mappen aangemaakt. Voeg een eerste map toe om te beginnen.
                    </p>
                ) : (
                    folders.map((folder) => (
                        <CompanyFolderStructureNode
                            key={folder.id}
                            folder={folder}
                            level={0}
                            allLabels={labels}
                            flatFolders={flatFolders}
                            refetch={handleRefetch}
                            onRequestLabelDelete={setLabelToDelete}
                            onRequestFolderDelete={setFolderToDelete}
                        />
                    ))
                )}
            </div>

            {/* Add folder modal */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
                <ModalContent>
                    <ModalHeader>Map toevoegen</ModalHeader>
                    <ModalBody className="space-y-4">
                        <Input
                            label="Naam"
                            placeholder="Voer een mapnaam in"
                            value={newFolder.name}
                            onChange={(e) =>
                                setNewFolder((prev) => ({ ...prev, name: e.target.value }))
                            }
                        />
                        <Input
                            label="Beschrijving (optioneel)"
                            placeholder="Voer een beschrijving in"
                            value={newFolder.description}
                            onChange={(e) =>
                                setNewFolder((prev) => ({ ...prev, description: e.target.value }))
                            }
                        />
                        {flatFolders.length > 0 && (
                            <Select
                                label="Bovenliggende map (optioneel)"
                                selectedKeys={newFolder.parentId ? [newFolder.parentId] : []}
                                onChange={(e) =>
                                    setNewFolder((prev) => ({
                                        ...prev,
                                        parentId: e.target.value,
                                    }))
                                }
                            >
                                {flatFolders.map((f) => (
                                    <SelectItem key={f.id.toString()}>{f.label}</SelectItem>
                                ))}
                            </Select>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="danger"
                            variant="light"
                            onPress={() => setIsAddModalOpen(false)}
                        >
                            Annuleren
                        </Button>
                        <Button
                            className="bg-moss-primary text-white"
                            onPress={handleAddFolder}
                            isDisabled={isLoading}
                        >
                            Toevoegen
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Confirm label deletion */}
            <Modal isOpen={!!labelToDelete} onClose={() => setLabelToDelete(null)}>
                <ModalContent>
                    <ModalHeader>Label verwijderen</ModalHeader>
                    <ModalBody>
                        <p className="text-neutral-primary">
                            Weet je zeker dat je het label{" "}
                            <strong>"{labelToDelete?.label}"</strong> wil verwijderen?
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="light" onPress={() => setLabelToDelete(null)}>
                            Annuleren
                        </Button>
                        <Button
                            color="danger"
                            onPress={handleConfirmLabelDelete}
                            isDisabled={isLoading}
                        >
                            Verwijderen
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Confirm folder deletion */}
            <Modal isOpen={!!folderToDelete} onClose={() => setFolderToDelete(null)}>
                <ModalContent>
                    <ModalHeader>Map verwijderen</ModalHeader>
                    <ModalBody>
                        <p className="text-neutral-primary">
                            Weet je zeker dat je de map{" "}
                            <strong>"{folderToDelete?.name}"</strong> wil verwijderen? Onderliggende
                            mappen en labels verdwijnen mee.
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="light" onPress={() => setFolderToDelete(null)}>
                            Annuleren
                        </Button>
                        <Button
                            color="danger"
                            onPress={handleConfirmFolderDelete}
                            isDisabled={isLoading}
                        >
                            Verwijderen
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
};

// ─── Helper ───────────────────────────────────────────────────────────────────

interface FlatFolder {
    id: number;
    label: string;
}

function flattenFolders(folders: CompanyFolderStructure[], depth = 0): FlatFolder[] {
    const result: FlatFolder[] = [];
    for (const folder of folders) {
        const indent = depth > 0 ? `${"  ".repeat(depth - 1)}└ ` : "";
        result.push({ id: folder.id, label: `${indent}${folder.name}` });
        if (folder.children && folder.children.length > 0) {
            result.push(...flattenFolders(folder.children, depth + 1));
        }
    }
    return result;
}
